import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import {subYears} from "date-fns";
import Content from "./components/content.jsx";
import Filters from "./components/filters.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import useReportFiltersFetch from "../../../hooks/use-report-filters-fetch.js";
const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subYears(new Date(), 1),
    dtEnd: new Date(),
    analysisType: "",
    reason: [],
    complement: [],
    cityFact: [],
    archivedOccurrences: true,
    corporationOnly: true,
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    analysisType: "Tipo de análise",
    reason: "Natureza",
    complement: "Classificação",
    cityFact: "Município do fato",
    archivedOccurrences: "Contabilizar ocorrências arquivadas",
    corporationOnly: "Apenas ocorrências da minha corporação",
};
const OccurrenceReasons = () => {
    const [loading, setLoading] = useState(false);
    const [title] = useState("Ocorrências por naturezas");
    const [reportData, setReportData] = useState(null);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const [reportDataLength, setReportDataLength] = useState(0)
    const buttonRef = useRef(null);
    const { occurrencesDetails } = useReportFiltersFetch({fetchOccurrencesDetails: true});

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            reason: occurrencesDetails
        }))
    }, [occurrencesDetails]);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, "year");

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            if (!filters.analysisType || filters.analysisType.length === 0) {
                ReportUtils.alertErrorMessage(`O campo "Tipo de análise" é obrigatório.`);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                analysisType: filters.analysisType?.value ?? "",
                reason: filters.reason?.map(i => i.label) ?? [],
                complement: filters.complement?.map(i => i.value) ?? [],
                cityFact: filters.cityFact?.map(i => i.value) ?? [],
                archivedOccurrences: filters.archivedOccurrences ?? false,
                corporationOnly: filters.corporationOnly ?? false
            };

            const auditFilters = ReportUtils.filters({
                    ...filters,
                    dtStart: formattedFilters.dtStart,
                    dtEnd: formattedFilters.dtEnd},
                filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/occurrence_reasons/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;

            if (filters.analysisType?.value === "MONTHLY_VIEW"){
                setReportDataLength(data.monthlyData.length)
            }else {
                setReportDataLength(data.weeklyData.length)
            }
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(prev => ({
            ...initialFilters(),
            reason: occurrencesDetails
        }));
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    useGlobalKey("Enter", apply)

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportDataLength}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters} occurrencesDetails={occurrencesDetails}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || reportDataLength === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportDataLength === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportDataLength === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={reportDataLength}/>
                            <Content reportData={reportData} data={reportDataLength} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} type={filters.analysisType?.value}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default OccurrenceReasons;